import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API } from 'utilities/api';
import { isPlatformHost } from 'config';
import PageNotFound from './PageNotFound';
import SiteRenderer from 'dashboard/components/siteRenderer/SiteRenderer';
import { StorefrontNav, useSeoMeta } from './StorefrontShop';
import { SiteHeader, SiteFooter } from './SiteFrame';
import NotFoundBody from './NotFoundBody';
import useSiteTheme from './useSiteTheme';

/**
 * Public brand site renderer.
 *
 * URL shapes supported:
 *   /site/:brandRef                  → homepage of the brand
 *   /site/:brandRef/:pageSlug        → specific published page
 *
 * `brandRef` is either a brand_id (24-char hex) or the brand's slug/domain.
 *
 * Once DNS white-label is wired up, the brand is resolved from the Host header
 * instead, and this component will be the default render at "/" for that host.
 */
const isObjectId = (s) => typeof s === 'string' && /^[a-f0-9]{24}$/i.test(s);

export default function PublicSite({ brandRef: brandRefProp, pageSlug: pageSlugProp, entrySlug: entrySlugProp } = {}) {
  // When mounted at /site/:brandRef[/:pageSlug] react-router gives us
  // params; when the host dispatcher mounts us at / it passes explicit
  // props instead. Props win.
  const params = useParams();
  const brandRef = brandRefProp ?? params.brandRef;
  const pageSlug = pageSlugProp ?? params.pageSlug;
  // Second path segment on a detail page — /blog/my-first-post.
  const entrySlug = entrySlugProp ?? params.entrySlug;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Header/footer fetched on their own when the page lookup fails.
  const [fallbackFrame, setFallbackFrame] = useState(null);

  // On a brand's own domain the path carries no :brandRef — acme.com/blog —
  // and the backend resolves the brand from the Host header anyway. So a
  // missing ref is normal there; the query simply omits it.
  //
  // On the platform host it is not normal: /some-typo would otherwise render
  // "this page hasn't been published yet" instead of a 404, which is both
  // wrong and confusing.
  const hostResolved = !brandRef && !isPlatformHost();

  useEffect(() => {
    if (!brandRef && !hostResolved) {
      setLoading(false);
      setError('Brand not specified');
      return;
    }
    setLoading(true);
    setError(null);
    const query = hostResolved
      ? { slug: pageSlug, entry_slug: entrySlug }
      : isObjectId(brandRef)
        ? { brand_id: brandRef, slug: pageSlug, entry_slug: entrySlug }
        : { brand: brandRef, slug: pageSlug, entry_slug: entrySlug };
    API.publicGetPage(query)
      .then((res) => { setData(res); setFallbackFrame(null); })
      .catch((err) => {
        setError(err?.message || 'Page not found');
        // A mistyped URL should still land the visitor inside the brand's
        // site — their menu, their colours, a way back — rather than on a
        // bare error screen that reads as the whole site being broken. The
        // page endpoint cannot supply the frame here, because there is no
        // page, so it is fetched on its own.
        const frameQuery = hostResolved
          ? {}
          : (isObjectId(brandRef) ? { brand_id: brandRef } : { brand: brandRef });
        API.publicSiteFrame(frameQuery)
          .then(setFallbackFrame)
          .catch(() => setFallbackFrame(null));
      })
      .finally(() => setLoading(false));
  }, [brandRef, pageSlug, entrySlug, hostResolved]);

  // Per-page SEO + share card. Falls back to the brand name when the
  // owner hasn't filled in page-level SEO fields.
  const page = data?.page;
  const brand = data?.brand;

  const themeCfg = (data?.page?.global || fallbackFrame?.global || {}).header?.themeToggle;
  const site = useSiteTheme(themeCfg?.defaultMode);
  const theme = themeCfg?.enabled !== false
    ? { enabled: true, isDark: site.isDark, toggle: site.toggle }
    : { enabled: false, isDark: false };
  useSeoMeta(brand && page ? {
    // On a detail page the entry is the subject, so it names the tab.
    title: page.detailEntry?.title
      || page.seoTitle
      || `${brand.name}${page.title ? ` — ${page.title}` : ''}`,
    description: page.seoDescription || '',
    keywords: page.seoKeywords || '',
    image: brand.logo_upload_path || '',
    type: 'website',
  } : {});

  // A bare path on the platform host is not a brand page; it is a wrong URL.
  if (!brandRef && !hostResolved) return <PageNotFound />;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', background: '#ffffff' }}>
        Loading…
      </div>
    );
  }

  if (error || !data) {
    const ff = fallbackFrame?.global || {};
    const fbName = fallbackFrame?.brand?.name;
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: theme.isDark ? '#0f1115' : '#ffffff' }}>
        <SiteHeader header={ff.header} brandName={fbName} brandLogo={fallbackFrame?.brand?.logo_upload_path} theme={theme} />
        <NotFoundBody notFound={ff.notFound} />
        <SiteFooter footer={ff.footer} brandName={fbName} theme={theme} />
      </div>
    );
  }

  // The brand's own header replaces our storefront nav when they have set
  // one. Showing both would stack two navigation bars on their site, and the
  // one they designed is the one that should win.
  const frame = data.page?.global || {};

  // Global design wins over a page's own header/footer sections.
  //
  // This was the other way round, on the reasoning that the more specific
  // thing should win. That was wrong in practice: every starter template adds
  // a header and a footer section, so every page created from one had its
  // own — and Global design, which an owner sets expecting it to apply
  // everywhere, applied nowhere. The menu they built simply never appeared.
  //
  // A page that genuinely wants its own frame can still say so with
  // suppressHeader / suppressFooter, which the server already honours.
  const globalHeader = frame.header;
  const globalFooter = frame.footer;
  const hasOwnHeader = Boolean(globalHeader);

  return (
    /**
     * The site paints its own ground.
     *
     * It used to leave the page background to whatever was behind it, which
     * was the app's `<body>` — and `<body>` is painted by the dashboard's
     * theme, through CssBaseline, because one React app serves both. That was
     * invisible while the dashboard was always light and its default happened
     * to be near-white. The moment the dashboard got a dark mode, an owner
     * with it switched on saw their own site's light mode come up black:
     * nothing to do with the visitor's choice, and not something a visitor
     * could ever reproduce.
     *
     * A public page must not take its colour from the dashboard's settings.
     */
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: theme.isDark ? '#0f1115' : '#ffffff',
      }}
    >
      {hasOwnHeader
        ? <SiteHeader header={globalHeader} brandName={brand?.name} brandLogo={brand?.logo_upload_path} theme={theme} />
        : <StorefrontNav brand={data.brand} />}
      <div style={{ flex: 1 }}>
        {/* The entry slug travels down so a detail page knows which of its
            siblings it is showing — that is what previous/next counts from
            and what "more like this" leaves out. */}
        <SiteRenderer
          page={data.page}
          brand={data.brand}
          isDark={theme.isDark}
          entrySlug={entrySlug || ''}
        />
      </div>
      <SiteFooter footer={globalFooter} brandName={brand?.name} theme={theme} />
    </div>
  );
}
